import { Task } from '@mui/icons-material'
import { darken, Grid, useTheme } from '@mui/material'
import {
  appleIcon,
  appleLogo,
  exchangeIcon,
  exchangeLogo,
  googleIcon,
  googleLogo,
  outlookIcon,
  outlookLogo
} from 'assets/icons/menu'
import { AxiosError } from 'axios'
import Button from 'components/buttons/Button'
import dateToTimeString from 'components/calendar/components'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import {
  parseErrorToMessage,
  RECUR_SAVE_TYPE
} from 'components/popups/eventInfo/EventPopup'
import { performUpdateGroupSettings } from 'components/popups/groupInfo/SettingsForCalendarSection'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import { Event } from 'microsoft-graph'
import { GroupPrivacy } from 'model/models/Group'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import Api from 'services/Api'
import { API_ADD_EVENT } from 'services/api/event'
import {
  addGroupToServer,
  genericUpdateFields,
  unlinkExternalCalendar,
  updateGroupColor,
  updateGroupSettings
} from 'services/api/group'
import Database from 'services/db/Database'
import {
  createEventFromAnotherEvent,
  excludeInstance,
  localEventByConvertedLocalId,
  localEventById,
  RemoveThirdPartyEvents,
  stopRecurrenceAt
} from 'services/db/Queries/Event'
import {
  localGroupById,
  ParticipantsAll,
  RemoveTempGroups,
  RemoveThirdPartyCalendar,
  setGroupSortOrder,
  setGroupSortOrderByEvent,
  thirdPartyProviderGroup
} from 'services/db/Queries/GroupQueries'
import { RemoveThirdPartyAccount } from 'services/db/Queries/ThirdPartyCalendarsQuieries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import IcloudApi from 'services/IcloudApi'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  DocType,
  EventDoc,
  EventStatus,
  GroupDoc,
  GroupStatus,
  PersonalCalendarGenericGroupSettings,
  TaskType
} from 'types/api/changes'
import { GoogleCalendar, GoogleEvent, ICalendar } from 'types/google_events'
import { CollectionType, Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  buildAddEventMessage,
  buildEditEventMessage,
  excludeInstanceFromRecurrence,
  handleEventUpdate,
  saveEventToLocalDB
} from 'utils/api/events'
import { addGoogleEvent } from 'utils/api/google_events'
import { syncLocalCalendarsToServer } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import { isGroupArchived, isGroupDeleted } from 'utils/api/userSettings'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { APP_COLOR, rgbStringToHex } from 'utils/colors'
import { outlookPrefix, prefixGoogleEventsGroup } from 'utils/constants'
import { isTestEnv } from 'utils/env'
import {
  connectGoogleCalendar,
  convertGoogleRemindersToGroupcalReminders,
  CurrentConvertedCalendars as CurrentConvertedCalendars,
  googleEventToGroupcal,
  groupcalEventToGoogleEvent,
  syncGoogleForDates
} from 'utils/google_events'
import {
  changeGroupStatus,
  groupIdToProvider,
  groupIdToProviderTypeName,
  groupToProvider,
  isConvertedGroupId,
  ProviderType
} from 'utils/groups'
import { isTrulyEmpty } from 'utils/object'
import {
  cacheConvertedCalendarsToLocalStorage,
  isIcloudEvent,
  isOutlookEvent,
  thirdPartyCalendarIdToPrimaryEmail
} from 'utils/third_party_calendars'
import { uuid } from 'utils/uuid'

import { useMidnightTrigger } from '../../hooks/useMidnightTrigger'
import { useIcloud } from './icloud/IcloudProvider'
import {
  outlookEventToTfmEvent,
  tfmEventToOutlook,
  useOutlookContext
} from './outlook/OutlookProvider'

export interface CalendarSyncContextProps {
  connect: (
    providerType: ProviderType,
    onComplete?: (calendar: ICalendar) => void,
    onCancel?: () => void,
    startedProcess?: () => void
  ) => void
  sync: (provider?: ProviderType, query?: string) => Promise<void>
  syncIcloud: (start: string, end: string) => void
  logout: (group: Model<GroupDoc>) => Promise<void>
  calendarTitle(thirdPartyId: string, group: GroupDoc | undefined): string
  loadEventDetails(eventId: string, provider: ProviderType): Promise<EventDoc>
  showProviderPicker?: () => void
  syncEventWithThirdParty: (
    instanceStart: number,
    instanceEnd: number,
    event: EventDoc,
    originalEvent: Model<EventDoc>,
    localSave: boolean,
    isDirty: boolean,
    onError?: (error: string) => void,
    type?: RECUR_SAVE_TYPE,
    shouldOverrideTime?: boolean
  ) => Promise<void>
  sendEventToServer: (
    originalEvent: Model<EventDoc> | EventDoc,
    data: EventDoc,
    localSave: boolean,
    isDirty: boolean,
    onError?: (error: string) => void
  ) => Promise<void>
  showProviderPickerDialog?: boolean
  setShowProviderPickerDialog?: (show: boolean) => void
  currentDate: string
  midnightCount: number
}

// Define the context for the provider
const CalendarSyncContext = createContext<CalendarSyncContextProps>({
  connect: () => {},
  sync: (provider?: ProviderType) => Promise.resolve(),
  syncIcloud: () => {},
  logout: (group: Model<GroupDoc>) => Promise.resolve(),
  calendarTitle: (thirdPartyId: string, group: GroupDoc) => 'test1',
  loadEventDetails: (eventId: string, provider: ProviderType) =>
    Promise.resolve({} as EventDoc),
  showProviderPicker: () => {},
  syncEventWithThirdParty: (
    instanceStart: number,
    instanceEnd: number,
    event: EventDoc,
    originalEvent: Model<EventDoc>,
    localSave: boolean,
    isDirty: boolean,
    onError?: (error: string) => void,
    type?: RECUR_SAVE_TYPE,
    shouldOverrideTime?: boolean
  ) => Promise.resolve(),
  sendEventToServer: (
    originalEvent: Model<EventDoc> | EventDoc,
    data: EventDoc,
    localSave: boolean,
    isDirty: boolean,
    onError?: (error: string) => void
  ) => Promise.resolve(),
  showProviderPickerDialog: false,
  setShowProviderPickerDialog: () => {},
  currentDate: new Date().toDateString(),
  midnightCount: 0
})

export const useCalendarSync = () => {
  return useContext(CalendarSyncContext)
}

export interface CalendarSyncProviderProps {
  children: React.ReactNode
}

export const CalendarSyncProvider = (props: CalendarSyncProviderProps) => {
  const outlook = useOutlookContext()
  const icloud = useIcloud()
  const [showProviderPickerDialog, setShowProviderPickerDialog] =
    useState(false)
  const [showNonGrantedCalendar, setShowNonGrantedCalendar] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<ProviderType | null>(
    null
  )

  cacheConvertedCalendarsToLocalStorage()
  // Trigger re-render and optional actions at midnight
  const { currentDate, midnightCount } = useMidnightTrigger({
    onMidnight: () => {
      Logger.debug(
        'CalendarSyncProvider',
        'Midnight triggered - new day started'
      )
      // Optional: trigger sync or other actions at midnight
      // sync()
    },
    enabled: true
  })

  const onCancel = useCallback(() => {
    setShowProviderPickerDialog(false)
    setShowNonGrantedCalendar(true)
  }, [selectedProvider])

  const connect = useCallback(
    (
      providerType: ProviderType,
      onComplete?: (calendar: ICalendar) => void,
      startedProcess?: () => void
    ) => {
      setSelectedProvider(providerType)

      // set initial dates for the app

      if (isTrulyEmpty(LocalStorage.get(LocalStorageKey.VISIBLE_FROM))) {
        LocalStorage.set(
          LocalStorageKey.VISIBLE_FROM,
          dayjs().startOf('week').startOf('day').toISOString()
        )
      }
      if (isTrulyEmpty(LocalStorage.get(LocalStorageKey.VISIBLE_TO))) {
        LocalStorage.set(
          LocalStorageKey.VISIBLE_TO,
          dayjs().endOf('week').endOf('day').toISOString()
        )
      }

      // Add your logic here to connect to the provider
      switch (providerType) {
        case ProviderType.GOOGLE:
          // Connect to Google
          connectGoogleCalendar(onComplete, onCancel, startedProcess)
          break
        case ProviderType.OUTLOOK:
          // Connect to Outlook
          if (outlook.signIn) outlook.signIn(onCancel)
          break
        // Add more cases here for other providers
        case ProviderType.APPLE:
          icloud.connect(onCancel)
          break
        default:
          throw new Error(`Unsupported provider type: ${providerType}`)
      }
    },
    []
  )

  const syncIcloud = (start: string, end: string, searchQuery?: string) => {
    return icloud.sync(start, end, undefined, searchQuery)
  }

  const showProviderPicker = useCallback(() => {
    setShowProviderPickerDialog(true)
  }, [])

  const sync = useCallback(async (provider?: ProviderType, query?: string) => {
    const start = LocalStorage.get(LocalStorageKey.VISIBLE_FROM) as string
    const end = LocalStorage.get(LocalStorageKey.VISIBLE_TO) as string

    if (LocalStorage.get(LocalStorageKey.USER_ID) === null) {
      // user not logged in, nothing to sync
      Logger.debug('CalendarSyncProvider', 'User not logged in, skipping sync')
      return
    }

    const syncPromises: Promise<void>[] = []

    if (!provider || provider === ProviderType.GOOGLE) {
      syncPromises.push(
        syncGoogleForDates(start, end, query).then(() => {
          Logger.debug('CalendarSyncProvider', 'Google sync done')
        })
      )
    }
    if (!provider || provider === ProviderType.OUTLOOK) {
      if (outlook.syncCalendars) {
        syncPromises.push(
          outlook.syncCalendars(start, end, false, query).then(() => {
            Logger.debug('CalendarSyncProvider', 'Outlook sync done')
          })
        )
      }
    }
    if (!provider || provider === ProviderType.APPLE) {
      syncPromises.push(
        syncIcloud(start, end, query).then(() => {
          Logger.debug('CalendarSyncProvider', 'iCloud sync done')
        })
      )
    }

    await Promise.all(syncPromises)
  }, [])

  const logout = useCallback(async (group: Model<GroupDoc>): Promise<void> => {
    // Add your logic here to log out from all providers
    const provider = groupToProvider(group)
    unlinkExternalCalendar(group)
      .then(() => {
        console.log('Unlinked calendar successfully', group)
        RemoveTempGroups()
      })
      .catch((error) => {
        Logger.red('Error during unlinking calendar', error)
        RemoveTempGroups()
      })
      .finally(() => {
        changeGroupStatus(group, GroupStatus.DELETED)
        const providerType = groupIdToProviderTypeName(group._id ?? '')
        if (provider === ProviderType.GOOGLE) {
          // do nothing
        } else if (provider === ProviderType.APPLE) {
          icloud.disconnect(group.CalendarEmail ?? '')
        } else if (provider === ProviderType.OUTLOOK) {
          outlook.signOut && outlook.signOut()
        }

        return Promise.all([
          RemoveThirdPartyEvents(group._id),
          RemoveThirdPartyCalendar(group._id),
          RemoveThirdPartyAccount(group.Name, providerType)
        ])
      })
  }, [])

  const calendarTitle = useCallback(
    (thirdPartyId: string, group: GroupDoc | undefined) => {
      const provider = groupToProvider(group)

      if (provider === ProviderType.OUTLOOK) {
        return (
          CurrentConvertedCalendars.find((calendar) => {
            return calendar.id === thirdPartyId
          })?.name ??
          group?.CalendarEmail?.replace(outlookPrefix, '') ??
          ''
        )
      } else if (provider === ProviderType.APPLE) {
        return (
          (
            CurrentConvertedCalendars.find((calendar) => {
              return calendar.id === thirdPartyId
            }) as ICalendar
          )?.name ??
          group?.CalendarEmail?.replace(prefixGoogleEventsGroup, '') ??
          ''
        )
      }

      return (
        (
          CurrentConvertedCalendars.find((calendar) => {
            return calendar.id === thirdPartyId
          }) as GoogleCalendar
        )?.summary ??
        group?.CalendarEmail?.replace(prefixGoogleEventsGroup, '') ??
        ''
      )
    },
    []
  )

  const loadEventDetails = async (
    eventId: string,
    provider?: ProviderType
  ): Promise<EventDoc> => {
    // for the outlook we need to download event details because of recurrence
    // if (provider === ProviderType.OUTLOOK && outlook.getEventDetails) {
    //   return outlook.getEventDetails(eventId).then((event: Event) => {
    //     return outlookEventToTfmEvent(event)
    //   })
    // }
    // for the rest of the events we can return local copy
    return localEventById(eventId)
  }

  async function handleEventCreate(
    event: Model<EventDoc> | EventDoc,
    data: EventDoc
  ) {
    if (
      is24meApp() &&
      (data.Type === DocType.TASK || data.Type === DocType.NOTE)
    ) {
      delete data['GroupID']
      delete data['ThirdPartyID']
      Database.write(async () => {
        if (event && typeof event.update === 'function') {
          event.update((local: { GroupID: undefined }) => {
            local.GroupID = undefined
          })
        }
      })
    }

    data = {
      ...data,
      _id: undefined,
      id: undefined,
      _rev: undefined,
      TaskType: String(data.TaskType) as TaskType,
      Status: EventStatus.ACTIVE,
      Priority: (data?.Priority?.length ?? 0) > 0 ? data.Priority : '1',
      Shared: 'null',
      PersonalOwnerId: data.PersonalOwnerId,
      OwnerID: data.OwnerID,
      Type: data.Type ?? (is24meApp() ? DocType.TASK : DocType.EVENT),
      GroupID:
        is24meApp() && data.TaskType !== TaskType.personalEvent
          ? undefined
          : data.GroupID,
      Rank: String(Date.now().valueOf() / 1000),
      ObjectType: '1',
      isDeleted: '0',
      UserID: LocalStorage.get(LocalStorageKey.USER_ID)!,
      TimeZoneNameID: data.AllDay === '1' ? 'UTC' : data.TimeZoneNameID,
      StartDate:
        data.someday === '1'
          ? 'null'
          : data.AllDay === '1'
            ? dayjs(Number(data.StartDate) * 1000)
                .tz('UTC')
                .startOf('day')
                .unix()
                .toString()
            : data.StartDate,
      EndDate:
        data.someday === '1'
          ? 'null'
          : data.AllDay === '1'
            ? dayjs(Number(data.EndDate) * 1000)
                .tz('UTC')
                .startOf('day')
                .unix()
                .toString()
            : data.EndDate
    }

    data = {
      ...data,
      Reminder: setAlertTimeToReminders(Number(data.StartDate), data.Reminder)
    }

    if (typeof data.Notes !== 'string') {
      data = {
        ...data,
        Notes: (data.Notes ?? []).map((note) => {
          return {
            ...note,
            FilePath: note.FilePath ? note.FilePath : 'null'
          }
        })
      }
    }

    if (isGroupcalApp()) delete data['Label']

    Logger.blue(
      'Data for event',
      data,
      'converted group? ',
      isConvertedGroupId(data.GroupID)
    )

    if (isConvertedGroupId(data.GroupID)) {
      const calendarId = data.ThirdPartyID
      const provider = groupIdToProvider(data.GroupID ?? '')

      Logger.debug('Adding event', data, ' to provider', provider)
      if (provider === ProviderType.GOOGLE) {
        await addGoogleEvent(
          calendarId?.replace(prefixGoogleEventsGroup, '') ?? '',
          groupcalEventToGoogleEvent(data, data.attendeesChanged === true)
        ).then((response) => {
          if (response.status === 200) {
            const googleCalendar = CurrentConvertedCalendars.find(
              (cal) =>
                cal.id === calendarId?.replace(prefixGoogleEventsGroup, '')
            )
            const googleEvent: GoogleEvent = response.data

            const eventWithMessage = buildAddEventMessage(
              LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
              {
                ...googleEventToGroupcal(response.data),
                Color: darken(
                  googleCalendar?.backgroundColor ?? APP_COLOR,
                  0.15
                ),
                ThirdPartyID: calendarId,
                GroupID: data.GroupID,
                Reminder: convertGoogleRemindersToGroupcalReminders(
                  googleEvent.reminders?.overrides,
                  data.StartDate ? Number(data.StartDate) * 1000 : 0
                ),
                needSync: '1'
              }
            )

            return saveEventToLocalDB(eventWithMessage)
          }
        })
      } else if (provider === ProviderType.OUTLOOK) {
        if (outlook.addEvent) {
          Logger.blue('Adding outlook event', data)
          const email = thirdPartyCalendarIdToPrimaryEmail(
            calendarId?.replace(outlookPrefix, '') ?? ''
          )
          await outlook
            .addEvent(
              await tfmEventToOutlook(data),
              calendarId?.replace(outlookPrefix, '') ?? ''
            )
            .then((response: Event) => {
              return outlook.getEventDetails?.(response.id)
            })
            .then((response: Event) => {
              Logger.red('Added outlook event', response)
              if (outlook.syncCalendars) outlook.syncCalendars()

              const tfmEventFromOutlook = outlookEventToTfmEvent(
                response,
                email!
              )

              const eventWithMessage = buildAddEventMessage(
                LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
                {
                  ...tfmEventFromOutlook,
                  PersonalOwnerId: thirdPartyCalendarIdToPrimaryEmail(
                    calendarId?.replace(outlookPrefix, '') ?? ''
                  ),
                  OwnerID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
                  GroupID: data.GroupID,
                  ThirdPartyID: calendarId?.replace(outlookPrefix, '') ?? '',
                  needSync: '1',
                  local_id: tfmEventFromOutlook._id,
                  LastUpdate: dayjs().unix().toString(),
                  ThirdPartyLastUpdate: dayjs().unix().toString()
                }
              )

              return saveEventToLocalDB(eventWithMessage).then(() => {
                return setGroupSortOrderByEvent(
                  eventWithMessage,
                  Number(eventWithMessage.LastUpdate)
                )
              })
            })
        }
      } else if (provider === ProviderType.APPLE) {
        const appleCalendar = CurrentConvertedCalendars.find(
          (iCalendar) => iCalendar.id === data.ThirdPartyID
        )
        const email = appleCalendar?.primaryCalendarEmail
        Logger.blue('Adding apple event', data, appleCalendar, email)
        if (typeof data.Location === 'string') delete data['Location']
        delete data['RequestConfirmation']
        if (appleCalendar && email) {
          const uniqueId = uuid()

          const calendarAttendees = data.attendees?.map((attendee) => {
            return {
              Email: attendee.email,
              ResponseStatus:
                attendee?.responseStatus?.toUpperCase() &&
                attendee?.responseStatus?.toLowerCase()?.includes('needs')
                  ? 'NEEDS-ACTION'
                  : attendee?.responseStatus?.toUpperCase()
            }
          })

          await IcloudApi.addCalendarItemToIcloud({
            email: email,
            calendar: appleCalendar.calDavString,
            event: {
              ...data,
              Attendees: calendarAttendees,
              _id: uniqueId,
              LastUpdate: dayjs().unix().toString()
            }
          }).then((added) => {
            Logger.red('Added apple event', added)

            const eventWithMessage = buildAddEventMessage(
              LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
              {
                ...data,
                Url: added[0].Url,
                OpenDate: added[0].OpenDate,
                Type: DocType.EVENT,
                TaskType: TaskType.personalEvent,
                OwnerID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
                UserID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
                PersonalOwnerId: email,
                GroupID: data.GroupID,
                ThirdPartyID: data.ThirdPartyID,
                Organizer: added[0].Organizer,
                needSync: '1',
                LastUpdate: added[0].LastUpdate,
                local_id: added[0]._id,
                _id: added[0]._id,
                Color: appleCalendar.backgroundColor ?? '#000000'
              }
            )

            return saveEventToLocalDB(eventWithMessage)
          })
        }
      }
      syncLocalCalendarsToServer()
    } else {
      const response = await Api.post(`${API_ADD_EVENT}`, {
        ...data,
        late: 0,
        attendeesChanged: 0,
        ...genericUpdateFields(),
        TaskType:
          data.TaskType?.length === 0 ? TaskType.meet : String(data.TaskType)
      })

      const serverData = response.data['doc']

      const eventWithMessage = buildAddEventMessage(
        LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
        {
          ...serverData,
          Priority: String(serverData['Priority']),
          TaskType: String(data.TaskType) as TaskType,
          someday: data.someday ?? '0'
        }
      )

      if (isGroupcalApp() && !isConvertedGroupId(eventWithMessage.GroupID)) {
        await setGroupSortOrderByEvent(
          eventWithMessage,
          Number(eventWithMessage.LastUpdate)
        )
      }

      await saveEventToLocalDB(eventWithMessage)
    }
  }

  const sendEventToServer = async (
    originalEvent: Model<EventDoc> | EventDoc,
    data: EventDoc,
    localSave: boolean,
    isDirty: boolean,
    onError?: (error: string) => void
  ) => {
    const local = await localEventById(originalEvent._id ?? '')

    // in case of local save only, for recurrent event we need to keep original start and end date
    if (localSave) {
      data.StartDate = local.StartDate
      data.EndDate = local.EndDate
    }

    if ((local?._id?.length ?? 0) > 0 || (local?.local_id?.length ?? 0) > 0) {
      return await handleEventUpdate(
        local,
        data,
        localSave,
        isDirty || data.Status != local.Status
      )
        .catch((e) => {
          console.error(e)
          if (isIcloudEvent(originalEvent)) {
            const start = LocalStorage.get(
              LocalStorageKey.VISIBLE_FROM
            ) as string
            const end = LocalStorage.get(LocalStorageKey.VISIBLE_TO) as string

            syncIcloud(start, end)
          } else if (isOutlookEvent(originalEvent)) {
            if (outlook.syncCalendars) outlook.syncCalendars()
          }
          if (onError) throw e
        })
        .finally(() => {
          LocalStorage.remove(LocalStorageKey.PENDING_EVENT_DATA)
        })
    } else {
      LocalStorage.remove(LocalStorageKey.PENDING_EVENT_DATA)
      return await handleEventCreate(local, data)
    }
  }

  const syncEventWithThirdParty = async (
    instanceStart: number,
    instanceEnd: number,
    data: EventDoc,
    originalEvent: Model<EventDoc>,
    localSave: boolean,
    isDirty: boolean,
    onError?: (error: string) => void,
    type?: RECUR_SAVE_TYPE,
    shouldOverrideTime?: boolean
  ) => {
    const provider = groupIdToProvider(data.GroupID ?? '')
    originalEvent = await localEventById(originalEvent._id ?? '')
    const local = await loadEventDetails(
      (isTrulyEmpty(originalEvent.ParentTaskID)
        ? originalEvent._id
        : originalEvent.ParentTaskID) ?? '',
      provider
    )
    Logger.blue('SyncEventWithThirdParty:Local', local)
    const tz = isTrulyEmpty(data.TimeZoneNameID)
      ? getDefaultTimezone()
      : data.TimeZoneNameID
    let localStart = dayjs(Number(local.StartDate) * 1000).tz(tz)
    let localEnd = dayjs(Number(local.EndDate) * 1000).tz(tz)

    const dataStart = dayjs(Number(data.StartDate) * 1000).tz(tz)
    const dataEnd = dayjs(Number(data.EndDate) * 1000).tz(tz)

    switch (type) {
      case RECUR_SAVE_TYPE.ALL:
        {
          if (shouldOverrideTime) {
            localStart = localStart.set('hour', dataStart.hour())
            localStart = localStart.set('minute', dataStart.minute())
            localEnd = localEnd.set('hour', dataEnd.hour())
            localEnd = localEnd.set('minute', dataEnd.minute())
          }

          await sendEventToServer(
            originalEvent,
            {
              ...data,
              StartDate: localStart.unix().toString(),
              EndDate: localEnd.unix().toString(),
              ParentTaskID: local.ParentTaskID,
              local_id: local.local_id
            },
            localSave,
            isDirty,
            onError
          )
        }
        break
      case RECUR_SAVE_TYPE.THIS:
        {
          try {
            Logger.blue('Recurrence saving data', data)
            delete data['Recurrence']

            if (provider === ProviderType.OUTLOOK) {
              /**
               * For outlook recurrent events, as we store it as a master event with it's parent id
               * when we edit and instance, we need to construct an occurence id to let outlook know which instance we are editing
               * Example:
               * parentId: AQMkADAwATM0MDAAMS0wYTY3LWJmODAtMDACLTAwCgBGAAAD04AicNjUlUSPZXQTQD_nFgcApEpDO0F_pU2C70SRadMCdAAAAgENAAAApEpDO0F_pU2C70SRadMCdAAHLrvJ0wAAAA==
               * then occurenceId will be:
               * "OID.AQMkADAwATM0MDAAMS0wYTY3LWJmODAtMDACLTAwCgBGAAAD04AicNjUlUSPZXQTQD_nFgcApEpDO0F_pU2C70SRadMCdAAAAgENAAAApEpDO0F_pU2C70SRadMCdAAHLrvJ0wAAAA==.2025-10-22"
               * where 2025-10-22 is the date of the instance we are editing
               */
              const occurenceId = originalEvent.local_id?.includes('OID')
                ? originalEvent.local_id
                : `OID.${originalEvent.local_id}.${dayjs(instanceStart).tz(data.TimeZoneNameID).format('YYYY-MM-DD')}`

              await sendEventToServer(
                originalEvent,
                {
                  ...data,
                  local_id: occurenceId
                },
                localSave,
                isDirty,
                onError
              ).then(() => {
                return excludeInstance(
                  instanceStart,
                  originalEvent,
                  isGroupcalApp()
                    ? EventStatus.REMOVED
                    : data.Status === EventStatus.ACTIVE
                      ? EventStatus.DELETED
                      : (data.Status ?? EventStatus.DELETED)
                )
              })
              return
            }

            if (data.Status === EventStatus.ACTIVE) {
              await handleEventCreate(originalEvent, data)
            }
            await excludeInstanceFromRecurrence(
              data,
              originalEvent,
              instanceStart,
              instanceEnd
            )
            LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
          } catch (e) {
            if (e instanceof AxiosError)
              if (onError) onError(parseErrorToMessage(e))
            Logger.red('during event update', e)
          }
        }
        break
      case RECUR_SAVE_TYPE.THIS_AND_FOLLOWING:
        if (data.Status === EventStatus.ACTIVE) {
          try {
            const copyOf = await createEventFromAnotherEvent(
              instanceStart,
              instanceEnd,
              local
            )
            await handleEventCreate(copyOf, {
              ...data,
              Recurrence: data.Recurrence,
              Status: EventStatus.ACTIVE // to keep original series state
            })
            LocalStorage.remove(LocalStorageKey.PENDING_EVENT_DATA)
            LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
          } catch (e) {
            if (e instanceof AxiosError)
              if (onError) onError(parseErrorToMessage(e))
            Logger.red('during event create: ', e)
          }
        }

        try {
          const dayjsTime = dayjs(instanceStart).add(-1, 'day').valueOf()
          const endDate = String(dayjsTime / 1000)

          // as we do not have parent events for outlook, we update the event itself
          const provider = groupIdToProvider(data?.GroupID ?? '')

          const excluded =
            provider === ProviderType.OUTLOOK
              ? {
                  ...data,
                  Recurrence: {
                    ...local?.Recurrence,
                    EndDate: endDate
                  }
                }
              : await stopRecurrenceAt(instanceStart, originalEvent)
          if (excluded)
            await sendEventToServer(
              originalEvent,
              {
                ...data,
                Text: local.Text,
                StartDate: localStart.unix().toString(),
                EndDate: localEnd.unix().toString(),
                Recurrence: excluded.Recurrence,
                ParentTaskID: local.ParentTaskID,
                local_id:
                  (local.ParentTaskID?.length ?? 0) > 0
                    ? local.ParentTaskID
                    : local.local_id
              },
              localSave,
              isDirty,
              onError
            ).then(() => {
              if (outlook.syncCalendars) outlook.syncCalendars()
            })
        } catch (e) {
          if (e instanceof AxiosError)
            if (onError) onError(parseErrorToMessage(e))
          Logger.red('during event update', e)
        }
        break
    }
  }

  const contextValue = useMemo(
    () => ({
      connect,
      sync,
      logout,
      calendarTitle,
      loadEventDetails,
      showProviderPicker,
      syncEventWithThirdParty,
      sendEventToServer,
      showProviderPickerDialog,
      setShowProviderPickerDialog,
      syncIcloud,
      currentDate,
      midnightCount
    }),
    [
      connect,
      sync,
      logout,
      calendarTitle,
      loadEventDetails,
      showProviderPicker,
      syncEventWithThirdParty,
      sendEventToServer,
      showProviderPickerDialog,
      setShowProviderPickerDialog,
      syncIcloud,
      currentDate,
      midnightCount
    ]
  )

  return (
    <CalendarSyncContext.Provider value={contextValue}>
      {props.children}
      <ConfirmationDialog
        title={t('selectYourCalendarAccount')}
        showCloseIcon={true}
        titlePadding="45px"
        content={
          <Grid
            display={'flex'}
            flexDirection={'column'}
            gap={'1.5rem'}
            alignItems={'center'}
            width={'100%'}
            paddingLeft={40}
            paddingRight={40}
          >
            <ProviderButton
              id="connect-apple-calendar-button"
              click={() => {
                connect(ProviderType.APPLE)
                setShowProviderPickerDialog(false)
              }}
              provider={ProviderType.APPLE}
              icon={appleIcon}
              logo={appleLogo}
            />
            <ProviderButton
              click={() => {
                connect(ProviderType.GOOGLE)
                setShowProviderPickerDialog(false)
              }}
              provider={ProviderType.GOOGLE}
              icon={googleIcon}
              logo={googleLogo}
            />
            <ProviderButton
              click={() => {
                connect(ProviderType.OUTLOOK)
                setShowProviderPickerDialog(false)
              }}
              provider={ProviderType.OUTLOOK}
              icon={outlookIcon}
              logo={outlookLogo}
            />
            <ProviderButton
              click={() => {
                connect(ProviderType.OUTLOOK)
                setShowProviderPickerDialog(false)
              }}
              provider={ProviderType.OUTLOOK}
              icon={exchangeIcon}
              logo={exchangeLogo}
            />
          </Grid>
        }
        handleClose={() => setShowProviderPickerDialog(false)}
        noButtons
        open={showProviderPickerDialog}
      />
      {showNonGrantedCalendar && (
        <ConfirmationDialog
          title={`${t('noGooglePermissionsTitle')}`}
          content={`${t('noGooglePermissionsMessage', {
            app: is24meApp() ? t('appName24me') : t('appName')
          })}`}
          rightButton={`${t('retry')}`}
          rightButtonColor={APP_COLOR}
          leftButton={`${t('cancel')}`}
          handleRightButton={() => {
            if (!selectedProvider) return
            setShowNonGrantedCalendar(false)
            connect(selectedProvider)
          }}
          handleLeftButton={() => {
            setShowNonGrantedCalendar(false)
          }}
          handleClose={() => {
            setShowNonGrantedCalendar(false)
          }}
          leftButtonColor="black"
          open={showNonGrantedCalendar}
        />
      )}
    </CalendarSyncContext.Provider>
  )
}

interface ProviderButtonProps {
  icon: string
  logo: string
  click: () => void
  id?: string
  provider?: ProviderType
}

export function ProviderButton(props: ProviderButtonProps) {
  const theme = useTheme()
  const invertAppleBranding =
    props.provider === ProviderType.APPLE && theme.palette.mode === 'dark'

  return (
    <Button id={props.id} onClick={props.click} width="250px">
      <Grid
        style={{ cursor: 'pointer' }}
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        gap={4}
      >
        <img
          src={props.icon}
          alt="icon"
          style={{ filter: invertAppleBranding ? 'invert(1)' : 'none' }}
        />
        <img
          style={{
            maxHeight: '38px',
            filter: invertAppleBranding ? 'invert(1)' : 'none'
          }}
          src={props.logo}
          alt="logo"
        />
      </Grid>
    </Button>
  )
}

export async function createGeneralGroupIfNeeded(
  prefix: string = prefixGoogleEventsGroup,
  id: string,
  color: string,
  message = 'Google Calendar',
  name = 'Google Calendar',
  withUnarchiving: boolean = false
): Promise<GroupDoc> {
  Logger.blue('CalendarSyncProvider', 'createGeneralGroupIfNeeded', {
    prefix,
    id,
    color
  })
  let groupInLocalDB = await thirdPartyProviderGroup(`${prefix}${id}`)

  Logger.blue('CalendarSyncProvider:Group', groupInLocalDB)
  Logger.blue('CalendarSyncProvider:WithUnarchiving', withUnarchiving)
  const us = await UserSettings()

  const shouldUpdateColor =
    groupInLocalDB?.GroupColor !==
    rgbStringToHex(
      darken(color.length > 0 ? rgbStringToHex(color) : APP_COLOR, 0.15)
    )

  function assignFields(local: GroupDoc, brandNew: boolean) {
    local.Name = name
    local.GroupSettings = PersonalCalendarGenericGroupSettings
    local.GroupColor = rgbStringToHex(
      darken(color.length > 0 ? rgbStringToHex(color) : APP_COLOR, 0.15)
    )
    local.CalendarEmail = `${prefix}${id}`
    local.CalendarType = prefix
    local._message = t('youAddedThisCalendar')
    local.SyncProblem = null
    if (brandNew) {
      local.LastUpdate = dayjs().add(10, 'seconds').unix().toString()
      local._raw['_sortOrder'] = dayjs().add(10, 'seconds').unix()
    }
    local.OwnerID = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    local.UserID = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    local._id = `${prefix}${id}`

    return local
  }

  Logger.blue('CalendarSyncProvider:Local group', groupInLocalDB)

  if (!groupInLocalDB) {
    groupInLocalDB = await Database.write(async () => {
      const createdGroup = await Database.getCollection<GroupDoc>(
        CollectionType.GROUP
      ).create((local) => assignFields(local, true))
      return createdGroup
    })
  } else {
    groupInLocalDB = await Database.write(async () => {
      return await groupInLocalDB!.update((local) => {
        local.SyncProblem = null
        local.CalendarEmail = `${prefix}${id}`
        local.CalendarType = prefix
        local.Name = name
        local.GroupColor = rgbStringToHex(
          darken(color.length > 0 ? rgbStringToHex(color) : APP_COLOR, 0.15)
        )
        local.LastUpdateMessage = {
          type:
            isTrulyEmpty(groupInLocalDB?.PrivateLinkPassword) &&
            isTrulyEmpty(groupInLocalDB?.PrivateLinkUrl)
              ? t('youAddedThisCalendar')
              : t('youSharedThisCalendar')
        }

        if (!local.GroupSettings) {
          local.GroupSettings = PersonalCalendarGenericGroupSettings
        }

        if (
          withUnarchiving &&
          (isGroupArchived(us, groupInLocalDB!._id ?? '') ||
            isGroupDeleted(us, groupInLocalDB!._id ?? ''))
        ) {
          Logger.blue('CalendarSyncProvider:Unarchiving')
          // unarchive group first

          local.LastUpdate = dayjs().unix().toString()
          local._raw['_sortOrder'] = Number(local.LastUpdate)
        }
      })
    })
  }

  if (
    withUnarchiving &&
    (isGroupArchived(us, groupInLocalDB._id ?? '') ||
      isGroupDeleted(us, groupInLocalDB._id ?? ''))
  ) {
    // unarchive group first

    await performUpdateGroupSettings(us, groupInLocalDB._id ?? '', {
      GroupStatus: GroupStatus.ACTIVE
    })

    // update permissions
    await updateGroupSettings(
      PersonalCalendarGenericGroupSettings,
      groupInLocalDB
    )
  }

  if (shouldUpdateColor && !isTrulyEmpty(groupInLocalDB._rev)) {
    await updateGroupColor(
      rgbStringToHex(
        darken(color.length > 0 ? rgbStringToHex(color) : APP_COLOR, 0.15)
      ),
      groupInLocalDB
    )
  }

  return groupInLocalDB!
}

export function addGroupModelToServer(groupInLocalDB: Model<GroupDoc>) {
  return addGroupToServer(groupInLocalDB, {
    color: rgbStringToHex(groupInLocalDB.GroupColor),
    setColor: function (color: string): void {},
    title: groupInLocalDB.Name,
    setTitle: function (title: string): void {},
    isCalendarChannel: groupInLocalDB.PrivacyMode === GroupPrivacy.CHANNEL,
    setIsCalendarChannel: function (isChannel: boolean): void {},
    link: groupInLocalDB.PrivateLinkUrl,
    setLink: function (link: string): void {},
    setPhoto: function (data: string): void {},
    photo: groupInLocalDB.Photo,
    metaDataState:
      groupInLocalDB.GroupSettings.IsAllParticipantsCanEditGroupMetadata,
    setMetaDataState: function (metaData: string): void {},
    allCanAddItemsState:
      groupInLocalDB.GroupSettings.IsAllParticipantsCanAddItems,
    setAllCanAddItemsState: function (allCanAddItems: string): void {},
    addParticipantsState:
      groupInLocalDB.GroupSettings.IsAllParticipantsCanAddParticipants,
    setAddParticipantsState: function (addParticipants: string): void {},
    eventOwnershipState:
      groupInLocalDB.GroupSettings.IsAllParticipantsCanEditOnlyTheirEvents,
    setEventOwnershipState: function (eventOwnership: string): void {},
    colors: [],
    createdGroup: undefined,
    setCreatedGroup: function (group: any): void {},
    eventsDetailsState: '',
    setEventsDetailsState: function (eventDetails: string): void {
      throw new Error('Function not implemented.')
    }
  })
}

/**
 *
 * @param eventsFromGoogle array of events from the google api
 * @param existingEvents existing locally events
 * @param converted received data from third party provider
 * @returns array of promises with pending changes to watermelondb
 */
export async function cacheOtherCalendarsEvents(
  data: any,
  existingEvents: EventDoc[],
  converted: EventDoc[],
  searchQuery?: string
) {
  if (!data) return []

  const promisesForDB: any[] = []

  const provider = groupIdToProvider(
    converted.length > 0 ? (converted[0].GroupID ?? '') : ''
  )

  await Promise.all(promisesForDB).then((events) => {
    Database.write(async () => {
      Database.batch(events)
    })
  })

  const collection = Database.getCollection<EventDoc>(CollectionType.EVENT)
  /**
   * per each converted item we either create or delete new items in local db
   */
  const promises: any[] = []

  let latestItem = 0

  const processedEventIds: string[] = []

  for (const existingItem of existingEvents) {
    // check for deleted events first
    const convertedItem = converted.find(
      (c) => c.local_id === existingItem.local_id
    )

    const existing = await localEventByConvertedLocalId(
      existingItem.local_id ?? ''
    )

    if (
      !isTrulyEmpty(existingItem.local_id) &&
      existingItem.Status === EventStatus.ACTIVE &&
      convertedItem === undefined &&
      searchQuery === undefined
    ) {
      if (processedEventIds.includes(existingItem.local_id ?? '')) {
        Logger.blue(
          'CalendarSync:Already processed deleted event',
          existingItem.Text
        )
        continue
      }

      // if we have converted item with same id, we do not delete it
      const lu = dayjs().unix().toString()

      Logger.blue('CalendarSyncProvider: Deleting event:', existingItem.Text)
      promises.push(
        existing.prepareUpdate((local) => {
          const eventStart = dayjs(Number(local.StartDate) * 1000)
          local.Status = EventStatus.DELETED
          local.LastUpdateMessage = {
            title: local.Text,
            type: `${t('eventDeleted')}`,
            who: `${t('you')}`,
            when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
              eventStart.toDate(),
              true
            )}`
          }
          local.ThirdPartyLastUpdate = lu
          local.LastUpdate = lu
          local.needSync = '1' // we need to sync this change to server
        })
      )
      latestItem = Math.max(latestItem, Number(lu))
      processedEventIds.push(existingItem.local_id ?? '')
    }

    if (
      existingItem.Status !== EventStatus.ACTIVE &&
      convertedItem !== undefined &&
      convertedItem.Status === EventStatus.ACTIVE
    ) {
      Logger.blue('CalendarSyncProvider:Fixing event state:', existingItem.Text)
      promises.push(
        existing.prepareUpdate((local) => {
          Object.keys(convertedItem).forEach((key) => {
            if (key !== '_id') {
              local[key] = convertedItem[key]
              local.Status = EventStatus.ACTIVE
              local.needSync = '1'
            }
          })
        })
      )
    }
  }

  const participants = await ParticipantsAll().fetch()

  if (converted.length > 0) {
    const provider = groupIdToProvider(converted[0].GroupID ?? '')

    for (const convertedItem of converted) {
      const existing = await localEventByConvertedLocalId(
        convertedItem.local_id ?? ''
      )

      let eventWithMessage = buildAddEventMessage(
        LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
        convertedItem
      )

      if (
        existing?.Status === EventStatus.DELETED &&
        convertedItem.Status === EventStatus.DELETED
      ) {
        Logger.orange(
          'CalendarSyncProvider:Event is deleted locally, skipping',
          existing.Text,
          existing
        )
        continue
      }

      const itemsSame = areItemsSame(existing, convertedItem, provider)

      if (itemsSame) {
        continue
      }

      if (
        (!existing ||
          existing.ThirdPartyLastUpdate !==
            convertedItem.ThirdPartyLastUpdate ||
          !itemsSame) &&
        latestItem < Number(convertedItem.ThirdPartyLastUpdate)
      ) {
        latestItem = Number(convertedItem.ThirdPartyLastUpdate)
      }

      Logger.blue('CalendarSyncProvider: Add to promises add/update')

      promises.push(
        existing
          ? existing.prepareUpdate((local) => {
              Object.keys(convertedItem).forEach((key) => {
                if (key !== '_id') {
                  local[key] = convertedItem[key]
                  local.Status = EventStatus.ACTIVE

                  local.Url = convertedItem.Url

                  if (
                    (provider === ProviderType.APPLE &&
                      existing.ThirdPartyLastUpdate !==
                        convertedItem.ThirdPartyLastUpdate) ||
                    !itemsSame
                  ) {
                    Logger.blue(
                      'CalendarSyncProvider:Will update',
                      provider,
                      existing.Text,
                      existing.ThirdPartyLastUpdate,
                      convertedItem.ThirdPartyLastUpdate
                    )
                    eventWithMessage = buildEditEventMessage(
                      LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
                      convertedItem,
                      existing,
                      participants
                    )
                    // we are setting needSync to 1 only if the event was changed on third party calendar
                    // this field will be reset once event synced to server
                    local.needSync = '1'
                    local.LastUpdateMessage = eventWithMessage.LastUpdateMessage
                    if (provider === ProviderType.GOOGLE) {
                      local.ThirdPartyLastUpdate =
                        convertedItem.ThirdPartyLastUpdate
                      local.LastUpdate = convertedItem.ThirdPartyLastUpdate
                      latestItem = Math.max(
                        latestItem,
                        Number(local.ThirdPartyLastUpdate)
                      )
                    }
                  }
                }
              })
            })
          : collection.prepareCreate((local) => {
              Object.keys(convertedItem).forEach((key) => {
                local[key] = convertedItem[key]
                local.Status = EventStatus.ACTIVE
                local.needSync = '1'
                local.LastUpdateMessage = {
                  ...eventWithMessage.LastUpdateMessage,
                  type: t('eventAdded')
                }
              })
            })
      )
    }

    if (promises.length > 0 && latestItem > 0) {
      // get local group first
      const group = await localGroupById(converted[0].GroupID ?? '')
      if (latestItem > group?._sortOrder)
        setGroupSortOrder(group._id, latestItem)
    }
  }

  Logger.blue('CalendarSyncProvider: promises', promises)

  await Promise.all(promises).then((events) => {
    Database.write(async () => {
      Database.batch(events)
    })
  })
}

export function setAlertTimeToReminders(
  startDate: number,
  Reminder: import('types/api/changes').EventReminder[] | undefined
) {
  if (!Reminder || Reminder.length === 0 || !Array.isArray(Reminder))
    return undefined

  return Reminder.map((reminder) => {
    if (!isTrulyEmpty(reminder.AlertTime)) return reminder

    return {
      ...reminder,
      AlertTime: (startDate - reminder.offset).toString()
    }
  })
}
/**
 * Deep comparison that handles property order differences
 * Sorts object keys before comparison to ensure order-independent comparison
 */
function deepCompareOrderIndependent(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true
  if (obj1 == null || obj2 == null) return obj1 === obj2
  if (typeof obj1 !== typeof obj2) return false
  if (typeof obj1 !== 'object') return obj1 === obj2

  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false
    return obj1.every((item, index) =>
      deepCompareOrderIndependent(item, obj2[index])
    )
  }

  // Handle objects - sort keys to make comparison order-independent
  const keys1 = Object.keys(obj1).sort()
  const keys2 = Object.keys(obj2).sort()

  if (keys1.length !== keys2.length) return false
  if (!keys1.every((key, index) => key === keys2[index])) return false

  return keys1.every((key) => deepCompareOrderIndependent(obj1[key], obj2[key]))
}

/**
 * Provider-specific field comparison configuration
 * Defines which fields to compare for each provider type
 */
type FieldComparisonKey = keyof typeof DEFAULT_COMPARISON_MAP

const DEFAULT_COMPARISON_MAP = {
  ThirdPartyLastUpdate: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      existing.ThirdPartyLastUpdate === converted.ThirdPartyLastUpdate,
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.ThirdPartyLastUpdate,
      converted: converted.ThirdPartyLastUpdate
    })
  },
  Text: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      existing.Text?.trimEnd() === converted.Text?.trimEnd(),
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.Text,
      converted: converted.Text
    })
  },
  StartDate: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      existing.StartDate === converted.StartDate,
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.StartDate,
      converted: converted.StartDate
    })
  },
  EndDate: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      existing.EndDate === converted.EndDate,
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.EndDate,
      converted: converted.EndDate
    })
  },
  Location: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) => {
      const existingLocation =
        existing && typeof existing.Location === 'string'
          ? existing.Location
          : existing?.Location?.Address || ''
      const convertedLocation =
        typeof converted.Location === 'string'
          ? converted.Location
          : converted.Location?.Address || ''
      return existingLocation === convertedLocation
    },
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => {
      const existingLocation =
        existing && typeof existing.Location === 'string'
          ? existing.Location
          : existing?.Location?.Address || ''
      const convertedLocation =
        typeof converted.Location === 'string'
          ? converted.Location
          : converted.Location?.Address || ''
      return { existing: existingLocation, converted: convertedLocation }
    }
  },
  Description: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) => {
      const existingDescription = existing?.Notes?.[0]?.Note || ''
      const convertedDescription = converted.Notes?.[0]?.Note || ''
      return existingDescription === convertedDescription
    },
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => {
      const existingDescription = existing?.Notes?.[0]?.Note || ''
      const convertedDescription = converted.Notes?.[0]?.Note || ''
      return { existing: existingDescription, converted: convertedDescription }
    }
  },
  AllDay: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      existing.AllDay === converted.AllDay,
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.AllDay,
      converted: converted.AllDay
    })
  },
  Status: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      existing.Status === converted.Status,
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.Status,
      converted: converted.Status
    })
  },
  NotesLength: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      existing.Notes?.length === converted.Notes?.length,
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.Notes?.length,
      converted: converted.Notes?.length
    })
  },
  Reminders: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      deepCompareOrderIndependent(existing.Reminder, converted.Reminder),
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.Reminder,
      converted: converted.Reminder
    })
  },
  Recurrence: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      deepCompareOrderIndependent(existing.Recurrence, converted.Recurrence),
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.Recurrence,
      converted: converted.Recurrence
    })
  },
  attendees: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      deepCompareOrderIndependent(existing.attendees, converted.attendees),
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.attendees,
      converted: converted.attendees
    })
  },
  ConferenceData: {
    compare: (existing: Model<EventDoc>, converted: EventDoc) =>
      deepCompareOrderIndependent(
        existing.ConferenceData,
        converted.ConferenceData
      ),
    getValues: (existing: Model<EventDoc>, converted: EventDoc) => ({
      existing: existing.ConferenceData,
      converted: converted.ConferenceData
    })
  }
} as const

/**
 * Get the fields to compare for a specific provider
 * Helps avoid initialization order issues with ProviderType enum
 */
function getFieldsToCompareForProvider(
  provider: ProviderType,
  existing: Model<EventDoc>,
  convertedItem: EventDoc
): FieldComparisonKey[] {
  switch (provider) {
    case ProviderType.GOOGLE:
      if (existing.PersonalOwnerId === 'readonly') {
        return ['ThirdPartyLastUpdate'] // Readonly events: only compare ThirdPartyLastUpdate to detect changes
      }

      return ['Reminders', 'Recurrence'] // Google: also compare recurrence to keep malformed source data in sync
    case ProviderType.OUTLOOK:
      return [
        'Text',
        'StartDate',
        'EndDate',
        'Location',
        'Description',
        'AllDay',
        'Status',
        'NotesLength',
        'Reminders',
        'Recurrence',
        'attendees',
        'ConferenceData'
      ] // Outlook: compare all fields
    case ProviderType.APPLE:
      return ['ThirdPartyLastUpdate'] // Apple: uses ThirdPartyLastUpdate check
    default:
      return []
  }
}

function areItemsSame(
  existing: Model<EventDoc> | undefined,
  convertedItem: EventDoc,
  provider: ProviderType | undefined
): boolean {
  // if we do not have an event, return false immediately
  if (!existing || !provider) return false

  // Get fields to compare for this provider
  const fieldsToCompare = getFieldsToCompareForProvider(
    provider,
    existing,
    convertedItem
  )

  if (existing.ThirdPartyLastUpdate !== convertedItem.ThirdPartyLastUpdate) {
    return false
  }

  // If no fields configured for this provider, all events are considered different
  if (fieldsToCompare.length === 0) {
    return false
  }

  // Build comparison map with only the relevant fields
  const comparisonMap: Record<
    string,
    { same: boolean; existing: any; converted: any }
  > = {}

  for (const fieldKey of fieldsToCompare) {
    const fieldConfig =
      DEFAULT_COMPARISON_MAP[fieldKey as keyof typeof DEFAULT_COMPARISON_MAP]
    if (fieldConfig) {
      const isSame = fieldConfig.compare(existing, convertedItem)
      const values = fieldConfig.getValues(existing, convertedItem)
      comparisonMap[fieldKey] = {
        same: isSame,
        ...values
      }
    }
  }

  const isBothEventsSame = Object.values(comparisonMap).every(
    (field) => field.same
  )

  if (!isBothEventsSame) {
    const differences = Object.entries(comparisonMap)
      .filter(([_, field]) => !field.same)
      .reduce(
        (acc, [key, field]) => {
          acc[key] = {
            existing: field.existing,
            converted: field.converted
          }
          return acc
        },
        {} as Record<string, { existing: any; converted: any }>
      )

    Logger.debug('CalendarSyncProvider:Event comparison - differences found:', {
      provider,
      eventId: existing._id || convertedItem._id,
      eventTitle: existing.Text || convertedItem.Text,
      fieldsCompared: fieldsToCompare,
      differences
    })
  }

  return isBothEventsSame
}
